<template>
  <button :class="buttonClasses" :disabled="disabled || loading" :type="type" @click="handleClick">
    <span v-if="loading" class="app-button__spinner">
      <svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="3"
          stroke-dasharray="32"
          stroke-dashoffset="32"
          opacity="0.3"
        />
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="3"
          stroke-dasharray="32"
          stroke-dashoffset="0"
          stroke-linecap="round"
        />
      </svg>
    </span>
    <span
      v-if="icon && !loading"
      :class="['app-button__icon', { 'app-button__icon--left': $slots.default }]"
    >
      <component :is="icon" />
    </span>
    <span v-if="!loading || $slots.default" class="app-button__content">
      <slot />
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface AppButtonProps {
  /** Вариант кнопки */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger'
  /** Размер кнопки */
  size?: 'small' | 'medium' | 'large'
  /** Кнопка заблокирована */
  disabled?: boolean
  /** Состояние загрузки */
  loading?: boolean
  /** Тип кнопки для форм */
  type?: 'button' | 'submit' | 'reset'
  /** Иконка (Vue component) */
  icon?: unknown
  /** Кнопка на всю ширину */
  fullWidth?: boolean
}

const props = withDefaults(defineProps<AppButtonProps>(), {
  variant: 'primary',
  size: 'medium',
  disabled: false,
  loading: false,
  type: 'button',
  fullWidth: false,
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClasses = computed(() => [
  'app-button',
  `app-button--${props.variant}`,
  `app-button--${props.size}`,
  {
    'app-button--full-width': props.fullWidth,
    'app-button--loading': props.loading,
    'app-button--disabled': props.disabled,
    'app-button--icon-only': props.icon && !props.loading && !slots.default,
  },
])

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

// Slots reference
import { useSlots } from 'vue'
const slots = useSlots()
</script>

<style scoped>
.app-button {
  /* Base styles */
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  border-radius: var(--radius-md, 0.625rem);
  font-family: var(--font-family-sans);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  white-space: nowrap;
  text-decoration: none;

  /* Typography */
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.43;
  letter-spacing: 0;
}

.app-button:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus, 0 0 0 3px rgba(37, 99, 235, 0.1));
}

/* ========== VARIANTS ========== */

/* Primary */
.app-button--primary {
  background-color: var(--color-primary, #2563eb);
  color: white;
  box-shadow: var(--shadow-button, 0 1px 2px 0 rgba(15, 23, 42, 0.05));
}

.app-button--primary:hover:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-primary-dark, #1d4ed8);
  box-shadow: var(--shadow-button-hover, 0 4px 6px -1px rgba(15, 23, 42, 0.1));
}

.app-button--primary:active:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-primary-dark, #1d4ed8);
  box-shadow: inset 0 2px 4px 0 rgba(15, 23, 42, 0.06);
}

/* Secondary */
.app-button--secondary {
  background-color: var(--color-surface, #ffffff);
  color: var(--color-text-primary, #18181b);
  border: 1px solid var(--color-border-strong, #e4e4e7);
  box-shadow: var(--shadow-button, 0 1px 2px 0 rgba(15, 23, 42, 0.05));
}

.app-button--secondary:hover:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-bg-muted, #f4f4f5);
}

.app-button--secondary:active:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-bg-muted, #f4f4f5);
}

/* Tertiary */
.app-button--tertiary {
  background-color: transparent;
  color: var(--color-primary, #2563eb);
  border: none;
  box-shadow: none;
}

.app-button--tertiary:hover:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-primary-tint, #eef2ff);
}

.app-button--tertiary:active:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-primary-tint, #eef2ff);
}

/* Danger */
.app-button--danger {
  background-color: var(--color-error, #ef4444);
  color: white;
  box-shadow: var(--shadow-button, 0 1px 2px 0 rgba(15, 23, 42, 0.05));
}

.app-button--danger:hover:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-error-dark, #dc2626);
  box-shadow: var(--shadow-button-hover, 0 4px 6px -1px rgba(15, 23, 42, 0.1));
}

.app-button--danger:active:not(:disabled):not(.app-button--loading) {
  background-color: var(--color-error-dark, #dc2626);
  box-shadow: inset 0 2px 4px 0 rgba(15, 23, 42, 0.06);
}

/* ========== SIZES ========== */

.app-button--small {
  height: var(--height-button-small, 2rem);
  padding: 0 0.75rem;
  font-size: 0.8125rem;
  gap: 0.375rem;
}

.app-button--medium {
  height: var(--height-button-medium, 2.5rem);
  padding: 0 1rem;
}

.app-button--large {
  height: var(--height-button-large, 3rem);
  padding: 0 1.5rem;
  font-size: 1rem;
  gap: 0.625rem;
}

/* ========== STATES ========== */

.app-button--full-width {
  width: 100%;
}

.app-button--disabled,
.app-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.app-button--loading {
  cursor: wait;
  pointer-events: none;
}

/* Icon only button */
.app-button--icon-only {
  padding: 0;
  aspect-ratio: 1;
}

/* ========== ELEMENTS ========== */

.app-button__spinner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.app-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
}

.app-button--small .app-button__icon {
  width: 1rem;
  height: 1rem;
}

.app-button--large .app-button__icon {
  width: 1.5rem;
  height: 1.5rem;
}

.app-button__icon--left {
  margin-right: -0.125rem;
}

.app-button__content {
  display: inline-flex;
  align-items: center;
}
</style>
